import React, { useEffect, useMemo, useState } from 'react';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import type {
  ConditionFieldOption,
  ConditionTree,
  NotificationCampaignType,
  PushNotificationCampaign,
  ScheduleType,
  TargetAudience,
} from '../../../models/MasterAdmin/pushNotificationCampaign';
import type { PushNotificationTemplate } from '../../../models/MasterAdmin/pushNotificationHub';
import { PUSH_HUB_BASE_PATH } from '../../../models/MasterAdmin/pushNotificationHub';
import {
  getDefaultScreenTypeForAudience,
  getIntentValueHint,
  getScreenTypeOptionsForAudience,
} from '../../../models/MasterAdmin/notificationScreenTypes';
import {
  createCampaign,
  estimateRecipientsPreview,
  fetchConditionOptions,
  getCampaign,
  sendCampaign,
  updateCampaign,
  type CampaignFormPayload,
} from '../../../services/MasterAdmin/pushNotificationCampaignService';
import { fetchPushTemplates } from '../../../services/MasterAdmin/pushNotificationHubService';
import { getApiErrorMessage } from '../../../utils/apiErrorMessage';
import ConditionBuilder from './ConditionBuilder';
import PushNotificationDevicePreview from './PushNotificationDevicePreview';
import PushPanelShell from './PushPanelShell';
import PushSearchSelect from './PushSearchSelect';
import PushMultiSearchSelect from './PushMultiSearchSelect';
import {
  AUDIENCE_HELPER_NOTES,
  PUSH_AUDIENCE_PILLS,
  SCHEDULE_TIMEZONES,
  inputClass,
  isPushPickerAudience,
} from './pushCampaignFormShared';
import { fetchUserAnalytics } from '../../../services/MasterAdmin/userService';
import { fetchVendorAuthActivity } from '../../../services/MasterAdmin/vendorService';
import WhatsAppRecipientSelectModal, {
  type RecipientSourceTab,
} from '../../WhatsApp/WhatsAppRecipientSelectModal';
import type { WhatsAppSendRecipient } from '../../../models/MasterAdmin/whatsappHub';
import { formatDisplayPhone } from '../../WhatsApp/whatsappUi';
import { recipientKey } from '../../WhatsApp/whatsappPreview';

export type PushCampaignEditorLocationState = {
  prefills?: Partial<PushNotificationCampaign> & {
    title?: string;
    message?: string;
    targetUserId?: string;
  };
};

type PlatformChip = 'ANDROID' | 'IOS' | 'WEB';

type FormState = {
  templateId: string;
  title: string;
  message: string;
  imageUrl: string | null;
  bannerImageUrl: string | null;
  notificationType: NotificationCampaignType;
  intentType: string;
  intentValue: string;
  audiences: TargetAudience[];
  platforms: PlatformChip[];
  filterCity: string;
  filterState: string;
  scheduleMode: 'now' | 'once' | 'recurring';
  scheduleType: ScheduleType;
  scheduledAt: string;
  scheduleTimezone: string;
  recurrenceTime: string;
  dayOfWeek: string;
  dayOfMonth: string;
  targetUserId: string;
  targetDisplayName: string;
  targetPhone: string;
  conditionTree: ConditionTree | null;
  behaviourSegmentKey: string;
};

function emptyForm(): FormState {
  return {
    templateId: '',
    title: '',
    message: '',
    imageUrl: null,
    bannerImageUrl: null,
    notificationType: 'OFFER',
    intentType: getDefaultScreenTypeForAudience('ALL_USERS'),
    intentValue: '',
    audiences: ['ALL_USERS'],
    platforms: ['ANDROID', 'IOS'],
    filterCity: '',
    filterState: '',
    scheduleMode: 'now',
    scheduleType: 'NOW',
    scheduledAt: '',
    scheduleTimezone: 'Asia/Kolkata',
    recurrenceTime: '10:00',
    dayOfWeek: '1',
    dayOfMonth: '1',
    targetUserId: '',
    targetDisplayName: '',
    targetPhone: '',
    conditionTree: null,
    behaviourSegmentKey: '',
  };
}

function platformsFromStored(raw: string | null | undefined): PlatformChip[] {
  const t = String(raw || 'BOTH').toUpperCase();
  if (t.includes(',')) {
    return (['ANDROID', 'IOS', 'WEB'] as const).filter((p) => t.split(',').map((x) => x.trim()).includes(p));
  }
  if (t === 'ANDROID') return ['ANDROID'];
  if (t === 'IOS') return ['IOS'];
  if (t === 'WEB') return ['WEB'];
  return ['ANDROID', 'IOS'];
}


function audiencesToStored(audiences: TargetAudience[]): string {
  const list = Array.from(new Set(audiences.filter(Boolean)));
  return list.length ? list.join(',') : 'ALL_USERS';
}

function audiencesFromStored(raw: string | null | undefined): TargetAudience[] {
  const parts = String(raw || 'ALL_USERS')
    .split(/[,\s|]+/)
    .map((p) => p.trim())
    .filter(Boolean) as TargetAudience[];
  return parts.length ? parts : (['ALL_USERS'] as TargetAudience[]);
}

function platformsToStored(platforms: PlatformChip[]): string {
  const set = new Set(platforms);
  const list = (['ANDROID', 'IOS', 'WEB'] as const).filter((p) => set.has(p));
  if (!list.length) return 'BOTH';
  if (list.length === 2 && list.includes('ANDROID') && list.includes('IOS')) return 'BOTH';
  if (list.length === 1) return list[0];
  return list.join(',');
}

function utcIsoToDatetimeLocal(iso: string, timeZone: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(d);
  const get = (type: string) => parts.find((p) => p.type === type)?.value || '';
  return `${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}`;
}

const PushCampaignEditorPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { campaignId } = useParams<{ campaignId?: string }>();
  const isEdit = Boolean(campaignId);
  const prefillsRef = React.useRef(
    (location.state as PushCampaignEditorLocationState | null)?.prefills
  );

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [templates, setTemplates] = useState<PushNotificationTemplate[]>([]);
  const [conditionFields, setConditionFields] = useState<ConditionFieldOption[]>([]);
  const [vendorStatus, setVendorStatus] = useState<'LOADING' | 'LOGGED_IN' | 'LOGGED_OUT' | null>(null);
  const [existingStatus, setExistingStatus] = useState<string | null>(null);
  const [recipientModalOpen, setRecipientModalOpen] = useState(false);
  const [estimateLoading, setEstimateLoading] = useState(false);
  const [estimateCount, setEstimateCount] = useState<number | null>(null);
  const [estimateNote, setEstimateNote] = useState<string | null>(null);

  const selectedTemplate = useMemo(
    () => templates.find((t) => t.id === form.templateId) || null,
    [templates, form.templateId]
  );

  const primaryAudience = form.audiences[0] || 'ALL_USERS';

  const screenOptions = useMemo(
    () => getScreenTypeOptionsForAudience(primaryAudience),
    [primaryAudience]
  );

  const userAudiences = [
    'ALL_USERS',
    'SINGLE_USER',
    'INACTIVE_USERS',
    'USERS_WITHOUT_HEALTH_RECORD',
    'LOGGED_OUT_USERS',
  ];

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const [tplRes, condRes] = await Promise.all([
          fetchPushTemplates({ activeOnly: true }),
          fetchConditionOptions().catch(() => ({ data: { fields: [] as ConditionFieldOption[] } })),
        ]);
        if (!active) return;
        setTemplates(tplRes.data?.items || []);
        setConditionFields(condRes.data?.fields || []);

        if (campaignId) {
          const camp = await getCampaign(campaignId);
          if (!active) return;
          const row = camp.data;
          setExistingStatus(row.status);
          const tz = row.scheduleTimezone || 'Asia/Kolkata';
          const st = String(row.scheduleType || (row.scheduledAt ? 'ONCE' : 'NOW')).toUpperCase() as ScheduleType;
          const recurring = ['DAILY', 'WEEKLY', 'EVERY_15_DAYS', 'MONTHLY'].includes(st);
          const cfg = (row.scheduleConfig || {}) as { time?: string; dayOfWeek?: number; dayOfMonth?: number };
          const aud = row.targetAudience || 'ALL_USERS';
          setForm({
            templateId: row.templateId || '',
            title: row.title || '',
            message: row.message || '',
            imageUrl: row.imageUrl,
            bannerImageUrl: row.bannerImageUrl || null,
            notificationType: row.notificationType || 'OFFER',
            intentType: row.intentType || 'USER_HOME',
            intentValue: row.intentValue || '',
            audiences: audiencesFromStored(row.targetAudience),
            platforms: platformsFromStored(row.targetPlatform),
            filterCity: row.filterCity || '',
            filterState: row.filterState || '',
            scheduleMode: recurring ? 'recurring' : row.scheduledAt || st === 'ONCE' ? 'once' : 'now',
            scheduleType: recurring ? st : st === 'ONCE' ? 'ONCE' : 'NOW',
            scheduledAt: row.scheduledAt ? utcIsoToDatetimeLocal(row.scheduledAt, tz) : '',
            scheduleTimezone: tz,
            recurrenceTime: cfg.time || '10:00',
            dayOfWeek: String(cfg.dayOfWeek ?? 1),
            dayOfMonth: String(cfg.dayOfMonth ?? 1),
            targetUserId: row.targetUserId || '',
            targetDisplayName: '',
            targetPhone: '',
            conditionTree: row.conditionTree || null,
            behaviourSegmentKey: row.behaviourSegmentKey || '',
          });
        } else if (prefillsRef.current) {
          const prefills = prefillsRef.current;
          const aud = (prefills.targetAudience || 'ALL_USERS') as TargetAudience;
          setForm((f) => ({
            ...f,
            title: prefills.title || '',
            message: prefills.message || '',
            notificationType: (prefills.notificationType as NotificationCampaignType) || 'OFFER',
            intentType: prefills.intentType || getDefaultScreenTypeForAudience(aud),
            intentValue: prefills.intentValue || '',
            audiences: audiencesFromStored(prefills.targetAudience || 'ALL_USERS'),
            platforms: platformsFromStored(prefills.targetPlatform || 'BOTH'),
            targetUserId: prefills.targetUserId || '',
            targetDisplayName: '',
            targetPhone: '',
            imageUrl: prefills.imageUrl || null,
            bannerImageUrl: prefills.bannerImageUrl || null,
            templateId: prefills.templateId || '',
          }));
        }
      } catch (err: unknown) {
        toast.error(getApiErrorMessage(err, 'Failed to load campaign editor'));
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [campaignId]);

  useEffect(() => {
    const isSingleVendor = ['SINGLE_DOCTOR', 'SINGLE_AMBULANCE', 'SINGLE_VENDOR'].includes(primaryAudience);
    const isSingleUser = primaryAudience === 'SINGLE_USER';
    const vId = form.targetUserId?.trim();
    if ((!isSingleVendor && !isSingleUser) || !vId) {
      setVendorStatus(null);
      return;
    }
    let active = true;
    setVendorStatus('LOADING');
    const req = isSingleUser ? fetchUserAnalytics(vId) : fetchVendorAuthActivity(vId);
    req
      .then((res: any) => {
        if (!active) return;
        const loggedIn = isSingleUser
          ? Boolean(res?.data?.authActivity?.isLoggedIn)
          : Boolean(res?.data?.isLoggedIn ?? res?.isLoggedIn);
        setVendorStatus(loggedIn ? 'LOGGED_IN' : 'LOGGED_OUT');
        if (!loggedIn) {
          const loginIntent =
            primaryAudience === 'SINGLE_USER'
              ? 'USER_LOGIN'
              : primaryAudience === 'SINGLE_DOCTOR'
                ? 'DOCTOR_LOGIN'
                : primaryAudience === 'SINGLE_AMBULANCE'
                  ? 'AMBULANCE_LOGIN'
                  : 'VENDOR_LOGIN';
          setForm((f) => ({ ...f, intentType: loginIntent }));
        }
      })
      .catch(() => {
        if (active) setVendorStatus(null);
      });
    return () => {
      active = false;
    };
  }, [primaryAudience, form.targetUserId, form.audiences]);

  const applyTemplate = (tpl: PushNotificationTemplate) => {
    setForm((f) => ({
      ...f,
      templateId: tpl.id,
      title: tpl.title,
      message: tpl.message,
      imageUrl: tpl.imageUrl,
      bannerImageUrl: tpl.bannerImageUrl || null,
      notificationType: tpl.notificationType as NotificationCampaignType,
      intentType: tpl.intentType || f.intentType,
      intentValue: tpl.intentValue || '',
    }));
  };


  const pickerSources = useMemo((): RecipientSourceTab[] => {
    if (primaryAudience === 'SINGLE_USER') return ['users'];
    if (primaryAudience === 'SINGLE_DOCTOR') return ['vendors'];
    if (primaryAudience === 'SINGLE_AMBULANCE') return ['vendors'];
    if (primaryAudience === 'SINGLE_VENDOR') return ['vendors'];
    return ['users', 'vendors'];
  }, [primaryAudience, form.audiences]);

  const selectedRecipients = useMemo((): WhatsAppSendRecipient[] => {
    if (!form.targetUserId) return [];
    const phoneDigits = form.targetPhone.replace(/\D/g, '').slice(-10) || '0000000000';
    const phone = form.targetPhone || formatDisplayPhone('+91', phoneDigits);
    return [
      {
        key: recipientKey('+91', phoneDigits),
        countryCode: '+91',
        phoneNumber: phoneDigits,
        displayName: form.targetDisplayName || 'Selected recipient',
        maskedPhone: phone,
        recipientId: form.targetUserId,
        source: primaryAudience === 'SINGLE_USER' ? 'user' : 'vendor',
        recipientType: primaryAudience === 'SINGLE_USER' ? 'user' : 'vendor',
      },
    ];
  }, [form.targetUserId, form.targetDisplayName, form.targetPhone, primaryAudience]);

  const onPickRecipient = (recipients: WhatsAppSendRecipient[]) => {
    const r = recipients[0];
    if (!r?.recipientId) {
      toast.error('Selected person has no linked account id for push.');
      return;
    }
    setForm((f) => ({
      ...f,
      targetUserId: r.recipientId || '',
      targetDisplayName: r.displayName || '',
      targetPhone: formatDisplayPhone(r.countryCode, r.phoneNumber),
    }));
    setRecipientModalOpen(false);
  };

  useEffect(() => {
    const needsId = form.audiences.some(isPushPickerAudience);
    if (needsId && !form.targetUserId.trim()) {
      setEstimateCount(null);
      setEstimateNote(null);
      return;
    }
    let cancelled = false;
    const timer = window.setTimeout(() => {
      setEstimateLoading(true);
      const platformsQuery = form.platforms.map((p) => p.toLowerCase()).join(',');
      void estimateRecipientsPreview({
        targetAudience: audiencesToStored(form.audiences),
        targetPlatform: platformsToStored(form.platforms),
        platforms: platformsQuery,
        filterCity: form.filterCity.trim() || undefined,
        filterState: form.filterState.trim() || undefined,
        targetUserId: form.targetUserId.trim() || undefined,
        conditionTree:
          form.audiences.some((a) => userAudiences.includes(a)) && form.conditionTree
            ? JSON.stringify(form.conditionTree)
            : undefined,
      })
        .then((res) => {
          if (cancelled) return;
          setEstimateCount(res.data.reachableFcmCount ?? res.data.estimatedRecipients ?? 0);
          setEstimateNote(res.data.note || null);
        })
        .catch(() => {
          if (cancelled) return;
          setEstimateCount(null);
          setEstimateNote('Could not estimate reach.');
        })
        .finally(() => {
          if (!cancelled) setEstimateLoading(false);
        });
    }, 400);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- estimate when targeting inputs change
  }, [
    form.audiences,
    form.platforms,
    form.filterCity,
    form.filterState,
    form.targetUserId,
    form.conditionTree,
  ]);

  const buildPayload = (saveAsDraft: boolean): CampaignFormPayload | null => {
    if (!form.templateId) {
      toast.error('Select a notification template. Title and message come from the template only.');
      return null;
    }
    if (!form.title.trim() || !form.message.trim()) {
      toast.error('Selected template is missing title or message.');
      return null;
    }
    if (
      ['SINGLE_USER', 'SINGLE_DOCTOR', 'SINGLE_AMBULANCE', 'SINGLE_VENDOR'].includes(primaryAudience) &&
      !form.targetUserId.trim()
    ) {
      toast.error('Target user / vendor ID is required for this audience.');
      return null;
    }
    const scheduleModeApi =
      form.scheduleMode === 'once' ? 'later' : form.scheduleMode === 'recurring' ? 'recurring' : 'now';
    const scheduleType =
      form.scheduleMode === 'now' ? 'NOW' : form.scheduleMode === 'once' ? 'ONCE' : form.scheduleType;
    const scheduleConfig =
      form.scheduleMode === 'recurring'
        ? JSON.stringify({
            time: form.recurrenceTime || '10:00',
            ...(form.scheduleType === 'WEEKLY' ? { dayOfWeek: Number(form.dayOfWeek) } : {}),
            ...(form.scheduleType === 'MONTHLY' ? { dayOfMonth: Number(form.dayOfMonth) } : {}),
            ...(form.scheduleType === 'EVERY_15_DAYS' ? { intervalDays: 15 } : {}),
          })
        : undefined;

    return {
      title: form.title.trim(),
      message: form.message.trim(),
      notificationType: form.notificationType,
      intentType: form.intentType,
      intentValue: form.intentValue.trim(),
      targetAudience: audiencesToStored(form.audiences),
      targetPlatform: platformsToStored(form.platforms) as CampaignFormPayload['targetPlatform'],
      filterCity: form.filterCity.trim(),
      filterState: form.filterState.trim(),
      scheduleMode: scheduleModeApi,
      scheduleType,
      scheduledAt:
        !saveAsDraft && (form.scheduleMode === 'once' || form.scheduleMode === 'recurring')
          ? form.scheduledAt
          : undefined,
      scheduleTimezone: form.scheduleTimezone || 'Asia/Kolkata',
      scheduleConfig,
      conditionTree:
        form.audiences.some((a) => userAudiences.includes(a)) && form.conditionTree
          ? JSON.stringify(form.conditionTree)
          : undefined,
      saveAsDraft,
      targetUserId: form.targetUserId.trim() || undefined,
      behaviourSegmentKey: form.behaviourSegmentKey.trim() || undefined,
      templateId: form.templateId,
      imageUrl: form.imageUrl || undefined,
      bannerImageUrl: form.bannerImageUrl || undefined,
    };
  };

  const goBack = () => navigate(PUSH_HUB_BASE_PATH);

  const onSave = async (saveAsDraft: boolean) => {
    const payload = buildPayload(saveAsDraft);
    if (!payload) return;
    setSaving(true);
    try {
      let campaignIdSaved = campaignId || '';
      if (isEdit && campaignId) {
        await updateCampaign(campaignId, payload);
        campaignIdSaved = campaignId;
        toast.success(saveAsDraft ? 'Draft saved.' : 'Campaign updated.');
      } else {
        const res = await createCampaign(payload);
        campaignIdSaved = res.data.id;
        toast.success(saveAsDraft ? 'Draft created.' : 'Campaign created.');
      }

      if (!saveAsDraft && form.scheduleMode === 'now' && campaignIdSaved) {
        const platforms = form.platforms.map((p) => p.toLowerCase()) as ('android' | 'ios' | 'web')[];
        const sendRes = await sendCampaign(campaignIdSaved, { platforms });
        toast.success(sendRes.data.message || 'Notification sent.');
        goBack();
        return;
      }
      goBack();
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err, 'Could not save campaign'));
    } finally {
      setSaving(false);
    }
  };

  const intentHint = getIntentValueHint(form.intentType);
  const navLocked =
    ['LOGGED_OUT_DOCTORS', 'LOGGED_OUT_AMBULANCE', 'LOGGED_OUT_VENDORS', 'LOGGED_OUT_USERS'].includes(
      primaryAudience
    ) ||
    (['SINGLE_DOCTOR', 'SINGLE_AMBULANCE', 'SINGLE_VENDOR', 'SINGLE_USER'].includes(primaryAudience) &&
      vendorStatus === 'LOGGED_OUT');

  if (loading) {
    return (
      <PushPanelShell title={isEdit ? 'Edit campaign' : 'Create campaign'}>
        <p className="text-sm text-gray-500">Loading…</p>
      </PushPanelShell>
    );
  }

  return (
    <PushPanelShell
      title={isEdit ? 'Edit campaign' : 'Create campaign'}
      description="Pick a template for content. Set audience, tap destination, conditions, and schedule here."
      actions={
        <button
          type="button"
          onClick={goBack}
          className="inline-flex items-center gap-1 rounded-lg border px-3 py-2 text-sm dark:border-gray-600"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          Back
        </button>
      }
    >
      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_320px]">
        <div className="space-y-5">
          {existingStatus === 'SENT' ? (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100">
              This campaign was already sent. Changes apply to future sends only.
            </div>
          ) : null}

          <section className="rounded-2xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">1. Template</h3>
            <p className="mt-1 text-xs text-gray-500">
              Title and message are taken from the template only — edit them under Templates.
            </p>
            <div className="mt-3">
              <PushSearchSelect
                value={form.templateId}
                onChange={(id) => {
                  const tpl = templates.find((t) => t.id === id);
                  if (tpl) applyTemplate(tpl);
                  else setForm((f) => ({ ...f, templateId: '' }));
                }}
                options={templates.map((t) => ({
                  value: t.id,
                  label: t.name,
                  hint: t.title,
                  group: t.notificationType,
                }))}
                placeholder="Select a template…"
                emptyLabel="Select a template…"
                searchPlaceholder="Search templates…"
                allowEmpty
              />
            </div>
            {!templates.length ? (
              <p className="mt-2 text-xs text-amber-600">
                No active templates.{' '}
                <button
                  type="button"
                  className="font-semibold underline"
                  onClick={() => navigate(`${PUSH_HUB_BASE_PATH}/templates/new`)}
                >
                  Create one first
                </button>
              </p>
            ) : null}
            {selectedTemplate ? (
              <div className="mt-3 rounded-xl bg-gray-50 p-3 text-sm dark:bg-gray-900/50">
                <p className="font-medium text-gray-900 dark:text-white">{form.title}</p>
                <p className="mt-1 whitespace-pre-wrap text-gray-600 dark:text-gray-300">{form.message}</p>
                {(form.bannerImageUrl || form.imageUrl) ? (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {form.bannerImageUrl ? (
                      <img src={form.bannerImageUrl} alt="" className="max-h-24 rounded-lg border object-cover" />
                    ) : null}
                    {form.imageUrl ? (
                      <img src={form.imageUrl} alt="" className="h-16 w-16 rounded-lg border object-cover" />
                    ) : null}
                  </div>
                ) : null}
              </div>
            ) : null}
          </section>

          <section className="rounded-2xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">2. Audience & platform</h3>
            <div className="mt-3">
              <label className="mb-1 block text-xs font-medium text-gray-600">Audience *</label>
              <PushMultiSearchSelect
                values={form.audiences}
                onChange={(vals) => {
                  const next = (vals.length ? vals : ['ALL_USERS']) as TargetAudience[];
                  const primary = next[0];
                  setForm((f) => ({
                    ...f,
                    audiences: next,
                    intentType: getDefaultScreenTypeForAudience(primary),
                    targetUserId: next.some(isPushPickerAudience) ? f.targetUserId : '',
                    targetDisplayName: next.some(isPushPickerAudience) ? f.targetDisplayName : '',
                    targetPhone: next.some(isPushPickerAudience) ? f.targetPhone : '',
                  }));
                }}
                options={PUSH_AUDIENCE_PILLS.map((a) => ({
                  value: a.value,
                  label: a.label,
                  group: a.value.includes('DOCTOR')
                    ? 'Doctors'
                    : a.value.includes('AMBULANCE')
                      ? 'Ambulance'
                      : a.value.includes('VENDOR') || a.value === 'DELIVERY_PARTNERS'
                        ? 'Vendors'
                        : 'Users',
                }))}
                placeholder="Select one or more audiences…"
                searchPlaceholder="Search audience…"
              />
              <p className="mt-2 text-xs text-blue-600 dark:text-blue-400">{AUDIENCE_HELPER_NOTES[primaryAudience]}</p>
            </div>

            {form.audiences.some(isPushPickerAudience) ? (

              <div className="mt-3 rounded-xl border border-gray-200 p-4 dark:border-gray-700">
                <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">Select recipient</p>
                    <p className="mt-1 text-xs text-gray-500">Search by name or phone — ID is stored in the background.</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setRecipientModalOpen(true)}
                    className="rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-700"
                  >
                    {form.targetUserId ? 'Change selection' : 'Open recipient list'}
                  </button>
                </div>
                {form.targetUserId ? (
                  <div className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-2 dark:border-gray-700 dark:bg-gray-900/50">
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {form.targetDisplayName || 'Selected recipient'}
                    </p>
                    <p className="text-xs text-gray-500">{form.targetPhone || 'Phone not available'}</p>
                  </div>
                ) : (
                  <p className="text-xs text-amber-600">Pick a person to continue.</p>
                )}
              </div>
            ) : null}

            <div className="mt-3">
              <label className="mb-1 block text-xs font-medium text-gray-600">Target platform *</label>
              <PushMultiSearchSelect
                values={form.platforms}
                onChange={(vals) => {
                  const next = (vals.length ? vals : ['ANDROID']) as PlatformChip[];
                  setForm((f) => ({ ...f, platforms: next }));
                }}
                options={[
                  { value: 'ANDROID', label: 'Android', group: 'Platform' },
                  { value: 'IOS', label: 'iOS', group: 'Platform' },
                  { value: 'WEB', label: 'Web', group: 'Platform' },
                ]}
                placeholder="Select one or more platforms…"
                searchPlaceholder="Search platform…"
              />
            </div>

            <div className="mt-4 rounded-xl border border-blue-100 bg-blue-50/80 px-4 py-3 dark:border-blue-900 dark:bg-blue-950/30">
              <p className="text-xs font-semibold uppercase tracking-wide text-blue-700 dark:text-blue-300">
                Estimated reach
              </p>
              {estimateLoading ? (
                <p className="mt-1 text-sm text-blue-800 dark:text-blue-200">Calculating…</p>
              ) : estimateCount == null ? (
                <p className="mt-1 text-sm text-blue-800/80 dark:text-blue-200/80">
                  {form.audiences.some(isPushPickerAudience)
                    ? 'Select a recipient to see count.'
                    : 'Fill audience & platform to see count.'}
                </p>
              ) : (
                <p className="mt-1 text-2xl font-bold text-blue-900 dark:text-blue-100">
                  {estimateCount.toLocaleString()}
                  <span className="ml-2 text-sm font-medium text-blue-700 dark:text-blue-300">devices</span>
                </p>
              )}
              {estimateNote ? <p className="mt-1 text-xs text-blue-700/80 dark:text-blue-300/80">{estimateNote}</p> : null}
            </div>

            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-600">City (optional)</label>
                <input className={inputClass} value={form.filterCity} onChange={(e) => setForm((f) => ({ ...f, filterCity: e.target.value }))} />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-600">State (optional)</label>
                <input className={inputClass} value={form.filterState} onChange={(e) => setForm((f) => ({ ...f, filterState: e.target.value }))} />
              </div>
            </div>
          </section>

          <section className="rounded-2xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">3. On tap → navigate to</h3>
            <p className="mt-1 text-xs text-gray-500">
              Controls where the app opens when the user taps the notification (intent / deep link handler).
            </p>
            <select
              className={`${inputClass} mt-3`}
              value={form.intentType}
              disabled={navLocked}
              onChange={(e) => setForm((f) => ({ ...f, intentType: e.target.value }))}
            >
              {screenOptions.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            {navLocked ? (
              <p className="mt-1.5 text-xs font-semibold text-amber-600">Locked to login because the target is logged out.</p>
            ) : null}
            <label className="mb-1 mt-3 block text-xs font-medium text-gray-600">Intent value / deep link data (optional)</label>
            <input
              className={inputClass}
              value={form.intentValue}
              onChange={(e) => setForm((f) => ({ ...f, intentValue: e.target.value }))}
              placeholder="Order id, URL, or JSON payload"
            />
            <p className="mt-1 text-xs text-gray-500">{intentHint}</p>
          </section>

          {form.audiences.some((a) => userAudiences.includes(a)) ? (
            <section className="rounded-2xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">4. Conditions (optional)</h3>
              <p className="mt-1 text-xs text-gray-500">Filter the audience after the base target is applied.</p>
              <div className="mt-3">
                <ConditionBuilder
                  value={form.conditionTree}
                  fields={conditionFields}
                  onChange={(tree) => setForm((f) => ({ ...f, conditionTree: tree }))}
                />
              </div>
            </section>
          ) : null}

                    <section className="rounded-2xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Behaviour analysis (optional)</h3>

            <div className="mt-4 rounded-xl border border-dashed border-gray-200 p-3 dark:border-gray-600">
              <label className="mb-1 block text-xs font-medium text-gray-600">Behaviour segment key (coming soon)</label>
              <input
                className={inputClass}
                value={form.behaviourSegmentKey}
                onChange={(e) => setForm((f) => ({ ...f, behaviourSegmentKey: e.target.value }))}
                placeholder="e.g. churn_risk_high — leave empty for now"
              />
              <p className="mt-1 text-[11px] text-gray-500">
                When user behaviour analysis goes live, this key will narrow the audience further. You can also use the
                “Behaviour segment” condition in the builder above.
              </p>
            </div>

          </section>

<section className="rounded-2xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
              {form.audiences.some((a) => userAudiences.includes(a)) ? '5' : '4'}. Schedule
            </h3>
            <div className="mt-3 flex flex-wrap gap-4">
              {([
                ['now', 'Send now'],
                ['once', 'Send once'],
                ['recurring', 'Recurring'],
              ] as const).map(([mode, label]) => (
                <label key={mode} className="inline-flex items-center gap-2 text-sm">
                  <input
                    type="radio"
                    name="sched"
                    checked={form.scheduleMode === mode}
                    onChange={() =>
                      setForm((f) => ({
                        ...f,
                        scheduleMode: mode,
                        scheduleType:
                          mode === 'now'
                            ? 'NOW'
                            : mode === 'once'
                              ? 'ONCE'
                              : f.scheduleType === 'NOW' || f.scheduleType === 'ONCE'
                                ? 'DAILY'
                                : f.scheduleType,
                      }))
                    }
                  />
                  {label}
                </label>
              ))}
            </div>
            <div className="mt-3">
              <label className="mb-1 block text-xs font-medium text-gray-600">Timezone</label>
              <select
                className={inputClass}
                value={form.scheduleTimezone}
                onChange={(e) => setForm((f) => ({ ...f, scheduleTimezone: e.target.value }))}
              >
                {SCHEDULE_TIMEZONES.map((tz) => (
                  <option key={tz.value} value={tz.value}>
                    {tz.label}
                  </option>
                ))}
              </select>
            </div>
            {form.scheduleMode === 'once' ? (
              <div className="mt-3">
                <label className="mb-1 block text-xs font-medium text-gray-600">Date & time</label>
                <input
                  type="datetime-local"
                  className={inputClass}
                  value={form.scheduledAt}
                  onChange={(e) => setForm((f) => ({ ...f, scheduledAt: e.target.value }))}
                />
              </div>
            ) : null}
            {form.scheduleMode === 'recurring' ? (
              <div className="mt-3 space-y-3">
                <select
                  className={inputClass}
                  value={form.scheduleType}
                  onChange={(e) => setForm((f) => ({ ...f, scheduleType: e.target.value as ScheduleType }))}
                >
                  <option value="DAILY">Daily</option>
                  <option value="WEEKLY">Weekly</option>
                  <option value="EVERY_15_DAYS">Every 15 days</option>
                  <option value="MONTHLY">Monthly</option>
                </select>
                {form.scheduleType === 'WEEKLY' ? (
                  <select className={inputClass} value={form.dayOfWeek} onChange={(e) => setForm((f) => ({ ...f, dayOfWeek: e.target.value }))}>
                    <option value="1">Monday</option>
                    <option value="2">Tuesday</option>
                    <option value="3">Wednesday</option>
                    <option value="4">Thursday</option>
                    <option value="5">Friday</option>
                    <option value="6">Saturday</option>
                    <option value="7">Sunday</option>
                  </select>
                ) : null}
                {form.scheduleType === 'MONTHLY' ? (
                  <input
                    type="number"
                    min={1}
                    max={31}
                    className={inputClass}
                    value={form.dayOfMonth}
                    onChange={(e) => setForm((f) => ({ ...f, dayOfMonth: e.target.value }))}
                  />
                ) : null}
                <input
                  type="time"
                  className={inputClass}
                  value={form.recurrenceTime}
                  onChange={(e) => setForm((f) => ({ ...f, recurrenceTime: e.target.value }))}
                />
              </div>
            ) : null}
          </section>

          <div className="flex flex-wrap gap-2 border-t border-gray-200 pt-4 dark:border-gray-700">
            <button
              type="button"
              disabled={saving}
              onClick={() => void onSave(true)}
              className="rounded-xl border border-gray-300 px-4 py-2.5 text-sm font-semibold dark:border-gray-600"
            >
              Save draft
            </button>
            <button
              type="button"
              disabled={saving}
              onClick={() => void onSave(false)}
              className="rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
            >
              {saving
                ? 'Sending…'
                : form.scheduleMode === 'now'
                  ? 'Save & send'
                  : 'Save & schedule'}
            </button>
          </div>
        </div>

        <aside className="xl:sticky xl:top-4 xl:self-start">
          <div className="rounded-2xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
            <p className="mb-3 text-sm font-semibold text-gray-900 dark:text-white">Live preview</p>
            <PushNotificationDevicePreview
              title={form.title}
              message={form.message}
              imageUrl={form.imageUrl}
              bannerImageUrl={form.bannerImageUrl}
            />
          </div>
        </aside>
      </div>

      <WhatsAppRecipientSelectModal
        open={recipientModalOpen}
        onClose={() => setRecipientModalOpen(false)}
        onConfirm={onPickRecipient}
        initialRecipients={selectedRecipients}
        singleSelect
        allowedSources={pickerSources}
        title="Select push recipient"
      />
    </PushPanelShell>
  );
};

export default PushCampaignEditorPage;
